<template>
  <div class="row-span-2 grid grid-cols-2 gap-x-4">
    <div v-for="field in templateFields" :key="field.name">
      <component
        :is="field.component"
        v-bind="omit(field, ['component', 'events'])"
        v-on="field.events"
      />
    </div>
  </div>
  <div class="my-2 grid">
    <lf-h3 class="mb-6">
      {{ $t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.ADDRESS") }}
    </lf-h3>
    <multiple-addresses v-model:addresses="allAddresses" :can-delete="false" />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import last from "lodash/last";
import omit from "lodash/omit";

import MultipleAddresses from "@/views/deals/components/MultipleAddresses.vue";
import MultipleInputsWithPrimaryValue from "@/views/deals/components/MultipleInputsWithPrimaryValue.vue";

import type { IBusinessEntityProfile } from "@/models/businesses";
import type { IOptions } from "@/models/options";
import type { IRootState } from "@/models/state";
import type { PropType } from "vue";

const emit = defineEmits<{
  (e: "primaryPhoneIdUpdated", newId: number): void;
  (e: "update:addresses", newId: IBusinessEntityProfile["addresses"]): void;
}>();

const props = defineProps({
  data: {
    type: Object as PropType<IBusinessEntityProfile | null>,
  },
  addresses: {
    type: Array as PropType<IBusinessEntityProfile["addresses"]>,
    required: true,
  },
  telephoneErrors: {
    type: Array as PropType<string[]>,
    required: true,
  },
});

const changePrimaryPhone = (id: number) => {
  emit("primaryPhoneIdUpdated", id);
};

const { getters } = useStore<IRootState>();
const { t } = useI18n();

const allAddresses = computed({
  get: () => props.addresses,
  set: (value) => emit("update:addresses", value),
});

const ein = computed(
  () => last(props.data?.piis?.filter(({ key }) => key === "EIN"))?.value || ""
);

const businessNumber = computed(
  () => last(props.data?.piis?.filter(({ key }) => key === "BN"))?.value || ""
);

const businessStartDate = computed(() =>
  String(props.data?.business_start_date || "").substring(0, 10)
);

const options = computed<IOptions>(() => getters["options/all"]);

const templateFields = computed(() => [
  {
    component: "lf-input",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.LEGAL_NAME"),
    value: props.data?.name || "",
    name: "business_legal_name",
  },
  {
    component: "lf-input",
    placeholder: t("DEALS.DEAL_PROGRESS.PERSONAL_INFO.DBA"),
    value: props.data?.doing_business_as || "",
    name: "doing_business_as",
  },
  {
    component: "lf-currency",
    placeholder: t("DEALS.TABLE_HEADERS.STATED_MONTHLY_REVENUE"),
    modelValue: props.data?.stated_monthly_revenue,
    name: "stated_monthly_revenue",
  },
  {
    component: "lf-dropdown",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.STRUCTURE_TYPE"),
    value: props.data?.business_entity_type_id || null,
    options: options.value.business_entity_types,
    name: "business_entity_type_id",
  },
  {
    component: "lf-input",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.START_DATE"),
    value: businessStartDate.value,
    name: "business_start_date",
    type: "date",
  },
  {
    component: "lf-dropdown",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.STATE_OF_INCORPORATION"),
    value: props.data?.formation_state || "",
    options: options.value.states,
    name: "formation_state",
  },
  {
    component: "lf-input",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.EIN"),
    value: ein.value,
    name: "piis.EIN",
  },
  {
    component: "lf-input",
    placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.BN"),
    value: businessNumber.value,
    name: "piis.BN",
  },
  {
    component: MultipleInputsWithPrimaryValue,
    name: "phone_numbers",
    isWfbObject: true,
    allValues: props.data?.phoneNumbers || [],
    placeholder: t("DEALS.DEAL_PROGRESS.PERSONAL_INFO.PHONE"),
    errorMessages: props.telephoneErrors,
    addMoreText: t("COMMON.ADD_PHONE"),
    canDelete: false,
    canUpdate: false,
    events: { primaryChanged: changePrimaryPhone },
  },
]);
</script>
